/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Grid, Typography, useTheme, useMediaQuery } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { ICustomTheme } from 'interfaces';

type Theme = {
  theme?: ICustomTheme;
};

const cssAlignCenter = css({
  alignItems: 'center',
});

const cssAlignLeft = css({
  alignItems: 'flex-start',
});

const cssDisplayColumn = css({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
});

const cssDisplayRow = css({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
});

const cssJustifyCenter = css({
  justifyContent: 'center',
});

const cssJustifyLeft = css({
  justifyContent: 'flex-start',
});

const Category = styled(Skeleton)<Theme>(({ theme }) => ({
  borderRadius: '2rem',
  height: 50,
  width: 200,
  [theme.breakpoints.down('sm')]: {
    width: 150,
  },
}));

const Icon = styled(Skeleton)<Theme>(({ theme }) => ({
  height: 40,
  marginRight: theme.spacing(2),
  width: 40,
}));

const Image = styled(Skeleton)<Theme>(({ theme }) => ({
  height: 400,
  marginBottom: theme.spacing(2),
  width: 500,
  [theme.breakpoints.down('sm')]: {
    height: 200,
    width: 200,
  },
}));

const Logo = styled(Skeleton)<Theme>(({ theme }) => ({
  height: 72,
  width: 72,
  [theme.breakpoints.down('sm')]: {
    height: 41,
    width: 41,
  },
}));

const Participation = styled(Typography)<Theme>(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
}));

const Sponsor = styled(Typography)<Theme>(({ theme }) => ({
  fontSize: theme.typography.h5.fontSize,
}));

const Subtitle = styled(Typography)<Theme>(({ theme }) => ({
  fontSize: theme.typography.h4.fontSize,
}));

const Teaser = styled(Typography)<Theme>(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
}));

const Title = styled(Typography)<Theme>(({ theme }) => ({
  fontSize: theme.typography.h3.fontSize,
  fontWeight: theme.typography.fontWeightBold,
}));

const ChallengeDescriptionSkeleton = (): JSX.Element => {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Grid container item css={cssDisplayColumn}>
      <Logo variant="circle" />
      <Skeleton>
        <Title>{t('challenges.detail.description.skeleton.title')}</Title>
      </Skeleton>
      <Category />

      <Grid
        container
        spacing={2}
        css={isDownSm ? cssDisplayColumn : cssDisplayRow}
      >
        <Grid item md={12} lg={5}>
          <Image />
        </Grid>

        <Grid
          item
          md={12}
          lg={7}
          css={[cssDisplayColumn, isDownSm ? cssAlignCenter : cssAlignLeft]}
        >
          <Skeleton>
            <Title>{t('challenges.detail.description.skeleton.title')}</Title>
          </Skeleton>
          <Skeleton>
            <Subtitle>
              {t('challenges.detail.description.skeleton.subtitle')}
            </Subtitle>
          </Skeleton>
          <Skeleton width={200}>
            <Sponsor>
              {t('challenges.detail.description.skeleton.sponsor')}
            </Sponsor>
          </Skeleton>
          <Skeleton>
            <Teaser>
              {t('challenges.detail.description.skeleton.teaser')}
            </Teaser>
          </Skeleton>

          <Grid
            container
            item
            css={[cssDisplayRow, isDownSm ? cssJustifyCenter : cssJustifyLeft]}
          >
            <Icon variant="circle" />
            <Skeleton>
              <Participation>
                {t('challenges.detail.description.skeleton.sponsor')}
              </Participation>
            </Skeleton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ChallengeDescriptionSkeleton;
